import {
  Component,
  DestroyRef,
  inject
} from '@angular/core';

import {
  takeUntilDestroyed
} from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap
} from 'rxjs';

import { Volume } from '../../../../models/volume';

import {
  selectCurrentVolume,
} from '../../../../features/volume/volume.selectors';

import {
  selectVolume,
  setVolumeShow
} from '../../../../features/volume/volume.actions';

import {
  selectMeasure
} from '../../../../features/measure/measure.actions';

import {
  selectDetection
} from '../../../../features/detection/detection.actions';

import {
  selectCut
} from '../../../../features/cut/cut.actions';

import {
  selectTrackerElevation,
  selectTrackerImage
} from '../../../../features/tracker/tracker.actions';

import {
  selectConfigDisplayVolumes,
  selectConfigItemDetailExpanded
} from '../../../../features/config/config.selectors';

import {
  setConfigExpandItemDetail
} from '../../../../features/config/config.actions';

import {
  selectCurrentSite, selectCurrentSiteBatchDate
} from '../../../../features/site/site.selectors';

import {
  setCameraConfiguration,
  setCameraMode
} from '../../../../features/cesium/cesium.actions';

import { CameraMode } from '../../../../enums/camera';
import { CesiumTools } from '../../../../tools/cesium.tools';

/*
 * Remplace cet import par l'import réel du service
 * qui contient la méthode resourceTmpUrl$().
 */
import { ApiService } from '../../../../services/api.service';
import {
  getMultipleValuesInSingleSelectionError
} from '@angular/cdk/collections';
import {
  selectCurrentReferenceBatchDate
} from '../../../../features/batch/batch.selectors';

interface VolumeViewerUrlsState {
  lasUrl: string | null;
  dxfUrl: string | null;
  isLoading: boolean;
  error: boolean;
}

@Component({
    selector: 'volume-details',
    templateUrl: './volume-details.component.html',
    styleUrls: ['../details.component.scss', './volume-details.component.scss'],
    standalone: false
})
export class VolumeDetailsComponent {
  private readonly destroyRef = inject(DestroyRef);

  volume: Volume | null | undefined;

  readonly displayVolumes$ =
    this.store.select(selectConfigDisplayVolumes);

  readonly isExpanded$ =
    this.store.select(selectConfigItemDetailExpanded);

  readonly viewerUrls$: Observable<VolumeViewerUrlsState>;

  readonly referenceBatchDate$ =
    this.store.select(
      selectCurrentReferenceBatchDate
    );

  readonly currentBatchDate$ =
    this.store.select(
      selectCurrentSiteBatchDate
    );

  constructor(
    private readonly store: Store,
    private readonly api: ApiService
  ) {
    this.viewerUrls$ = this.createViewerUrlsState();
    this.initHandleCurrentVolume();
  }

  private initHandleCurrentVolume(): void {
    this.store
      .select(selectCurrentVolume)
      .pipe(
        tap((volume: Volume | null | undefined) => {
          const previousVolumeId = this.volume?.id;

          // Always synchronize the local volume with NgRx
          this.volume = volume;

          if (!volume) {
            return;
          }

          // Clear related selections only when switching volumes
          if (previousVolumeId !== volume.id) {
            this.clearRelatedSelections();
          }
        }),

        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private createViewerUrlsState(): Observable<VolumeViewerUrlsState> {
    return combineLatest([
      this.store.select(selectCurrentVolume),
      this.store.select(selectCurrentSite)
    ]).pipe(
      distinctUntilChanged(
        (
          previous,
          current
        ): boolean => {
          const [previousVolume, previousSite] = previous;
          const [currentVolume, currentSite] = current;

          return (
            previousVolume?.id === currentVolume?.id &&
            previousVolume?.lazPath === currentVolume?.lazPath &&
            previousVolume?.dxfPath === currentVolume?.dxfPath
          );
        }
      ),

      switchMap(([volume, site]) => {
        if (!volume || !site) {
          return of<VolumeViewerUrlsState>({
            lasUrl: null,
            dxfUrl: null,
            isLoading: false,
            error: false
          });
        }

        if (!volume.lazPath || !volume.dxfPath) {
          console.error(
            'Impossible de charger le viewer : ' +
            'lazPath ou dxfPath est absent.',
            {
              volumeId: volume.id,
              lazPath: volume.lazPath,
              dxfPath: volume.dxfPath
            }
          );

          return of<VolumeViewerUrlsState>({
            lasUrl: null,
            dxfUrl: null,
            isLoading: false,
            error: true
          });
        }

        const requests$ = {
          lasUrl: this.createTemporaryUrl(
            site.organization,
            volume.lazPath
          ),

          dxfUrl: this.createTemporaryUrl(
            site.organization,
            volume.dxfPath
          )
        };


        return forkJoin(requests$).pipe(
          map(
            ({
              lasUrl,
              dxfUrl
            }): VolumeViewerUrlsState => ({
              lasUrl,
              dxfUrl,
              isLoading: false,
              error: lasUrl === null || dxfUrl === null
            })
          ),

          catchError((error: unknown) => {
            console.error(
              'Impossible de générer les URLs temporaires ' +
              'du fichier LAZ et du fichier DXF.',
              error
            );

            return of<VolumeViewerUrlsState>({
              lasUrl: null,
              dxfUrl: null,
              isLoading: false,
              error: true
            });
          }),

          startWith<VolumeViewerUrlsState>({
            lasUrl: null,
            dxfUrl: null,
            isLoading: true,
            error: false
          })
        );
      }),

      shareReplay({
        bufferSize: 1,
        refCount: true
      }),

      takeUntilDestroyed(this.destroyRef)
    );
  }

  private createTemporaryUrl(
    organizationId: string,
    path: string
  ): Observable<string | null> {
    const filename = this.getFilenameFromPath(path);

    return this.api
      .resourceTmpUrl$(
        organizationId,
        path,
        filename
      )
      .pipe(
        map((url: string | null) => {
          if (!url) {
            console.error(
              'Aucune URL temporaire retournée.',
              {
                organizationId,
                path,
                filename
              }
            );
          }

          return url;
        }),

        catchError((error: unknown) => {
          console.error(
            'Erreur lors de la génération de l’URL temporaire.',
            {
              organizationId,
              path,
              filename,
              error
            }
          );

          return of(null);
        })
      );
  }

  private getFilenameFromPath(path: string): string {
    const pathWithoutQuery = path
      .split('?')[0]
      .split('#')[0];

    const normalizedPath = pathWithoutQuery
      .replace(/\\/g, '/')
      .replace(/\/+$/, '');

    const segments = normalizedPath
      .split('/')
      .filter((segment: string) => segment.length > 0);

    const lastSegment =
      segments.length > 0
        ? segments[segments.length - 1]
        : null;

    if (!lastSegment) {
      return 'resource';
    }

    try {
      return decodeURIComponent(lastSegment);
    } catch {
      return lastSegment;
    }
  }

  private clearRelatedSelections(): void {
    this.store.dispatch(
      selectMeasure({
        id: undefined
      })
    );

    this.store.dispatch(
      selectDetection({
        id: undefined
      })
    );

    this.store.dispatch(
      selectCut({
        id: undefined
      })
    );

    this.store.dispatch(
      selectTrackerImage({
        id: undefined
      })
    );

    this.store.dispatch(
      selectTrackerElevation({
        id: undefined
      })
    );
  }

  onClickFlyTo(volume: Volume): void {
    const cameraConfiguration =
      CesiumTools.cameraConfigurationFromTargetAndDirection(
        volume.center,
        volume.cameraDirection,
        volume.length
      );

    if (!cameraConfiguration) {
      return;
    }

    this.store.dispatch(
      setCameraMode({
        cameraMode: CameraMode.MODE_3D
      })
    );

    this.store.dispatch(
      setCameraConfiguration({
        cameraConfiguration
      })
    );
  }

  onClickToggleShow(volume: Volume): void {
    this.store.dispatch(
      setVolumeShow({
        id: volume.id,
        show: !volume.show
      })
    );
  }

  onClickClose(): void {
    this.store.dispatch(
      setConfigExpandItemDetail({
        expand: false
      })
    );

    this.store.dispatch(
      selectVolume({
        id: undefined
      })
    );
  }

  onClickExpand(): void {
    this.store.dispatch(
      setConfigExpandItemDetail({
        expand: true
      })
    );
  }

  onClickCollapse(): void {
    this.store.dispatch(
      setConfigExpandItemDetail({
        expand: false
      })
    );
  }
}
